/**
 * DashboardTab — Super Admin overview KPIs
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, CreditCard, DollarSign, AlertTriangle, Inbox, Building2, TrendingUp } from 'lucide-react';
import { superAdminApi } from './superAdminApi';

const KPICard = ({ icon: Icon, label, value, accent = 'indigo', sub, testid }) => (
  <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white" data-testid={testid}>
    <CardContent className="pt-5 pb-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-400">{label}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
        </div>
        <div className={`w-11 h-11 rounded-xl bg-${accent}-500/20 border border-${accent}-400/30 flex items-center justify-center`}>
          <Icon className={`h-5 w-5 text-${accent}-300`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export const DashboardTab = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['superadmin-dashboard'],
    queryFn: superAdminApi.dashboard,
    refetchInterval: 30000,
  });

  if (isLoading) return <div className="text-slate-400 p-8">Loading dashboard...</div>;
  const d = data || {};

  return (
    <div className="space-y-6" data-testid="superadmin-dashboard-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard icon={Building2} label="Total Tenants" value={d.tenants?.total ?? 0} accent="indigo" testid="kpi-total-tenants" />
        <KPICard icon={Users} label="Active Subs" value={d.tenants?.active_subscriptions ?? 0} accent="emerald" testid="kpi-active-subs" />
        <KPICard icon={AlertTriangle} label="Trials End <7d" value={d.tenants?.trials_ending_in_7d ?? 0} accent="amber" testid="kpi-trials-ending" />
        <KPICard icon={Inbox} label="Applications" value={(d.applications?.submitted ?? 0) + (d.applications?.under_review ?? 0)} accent="fuchsia"
                 sub={`${d.applications?.submitted ?? 0} new · ${d.applications?.under_review ?? 0} review`} testid="kpi-applications" />
        <KPICard icon={DollarSign} label="Revenue (30d)" value={`$${d.revenue?.last_30_days ?? 0}`} accent="emerald" testid="kpi-revenue-30d" />
        <KPICard icon={TrendingUp} label="Revenue (Total)" value={`$${d.revenue?.total ?? 0}`} accent="emerald" testid="kpi-revenue-total" />
        <KPICard icon={CreditCard} label="Unpaid Invoices" value={d.revenue?.unpaid_count ?? 0} accent="rose"
                 sub={`$${d.revenue?.unpaid_amount ?? 0} outstanding`} testid="kpi-unpaid" />
        <KPICard icon={Users} label="Top Tenant Students" value={d.top_tenants_by_students?.[0]?.students ?? 0} accent="sky"
                 sub={d.top_tenants_by_students?.[0]?.name ?? '—'} testid="kpi-top-tenant" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
          <CardHeader>
            <CardTitle className="text-base">Tenants by Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(d.tenants?.by_status || {}).length === 0 && <p className="text-sm text-slate-500">No tenants yet.</p>}
            {Object.entries(d.tenants?.by_status || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between p-2 rounded-lg bg-white/5">
                <Badge variant="outline" className="capitalize text-slate-200 border-white/20">{k}</Badge>
                <span className="font-bold">{v}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
          <CardHeader>
            <CardTitle className="text-base">Plan Distribution</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(d.plan_distribution || {}).length === 0 && <p className="text-sm text-slate-500">No active subscriptions yet.</p>}
            {Object.entries(d.plan_distribution || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between p-2 rounded-lg bg-white/5">
                <Badge variant="outline" className="capitalize text-indigo-200 border-indigo-400/30 bg-indigo-500/10">{k}</Badge>
                <span className="font-bold">{v}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
        <CardHeader>
          <CardTitle className="text-base">Top Tenants by Students</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(d.top_tenants_by_students || []).slice(0, 10).map((t, i) => (
              <div key={t.tenant_id} className="flex items-center gap-3 p-2 rounded-lg bg-white/5">
                <span className="text-xs font-mono text-slate-500 w-6">#{i + 1}</span>
                <div className="flex-1">
                  <p className="text-sm font-semibold">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.code}</p>
                </div>
                <Badge className="bg-indigo-500/20 text-indigo-200 border-indigo-400/30">{t.students} students</Badge>
              </div>
            ))}
            {(!d.top_tenants_by_students || d.top_tenants_by_students.length === 0) &&
              <p className="text-sm text-slate-500">No tenants yet.</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
